import axios from "axios";
import * as Sentry from "@sentry/react";
import { getFreshAccessToken } from "./tokenProvider";
const DEFAULT_ERR_MESSAGE = "Unexpected Error Occurred. Please try again later."

/**
 * Turn an axios error body into text we can show in a toast.
 *
 * A request made with responseType "blob" gets a Blob back even when it fails, so the body
 * has to be read before it says anything -- rejecting it as it comes shows "[object Blob]".
 * The API renders a plain string body as JSON, so "No data found" arrives as '"No data
 * found"'; parsing it drops the quotes. Anything we cannot read falls back to the default.
 */
const readErrorMessage = async (data) => {
	let body = data;

	if (body instanceof Blob) {
		try {
			body = await body.text();
		} catch {
			return DEFAULT_ERR_MESSAGE;
		}
	}

	if (typeof body === "string") {
		const text = body.trim();
		if (!text) return DEFAULT_ERR_MESSAGE;
		try {
			const parsed = JSON.parse(text);
			if (typeof parsed === "string") return parsed.trim() || DEFAULT_ERR_MESSAGE;
			if (parsed && typeof parsed === "object") {
				return parsed.detail || parsed.message || parsed.error || DEFAULT_ERR_MESSAGE;
			}
		} catch {
			return text;
		}
		return text;
	}

	if (body && typeof body === "object") {
		return body.detail || body.message || body.error || DEFAULT_ERR_MESSAGE;
	}

	return DEFAULT_ERR_MESSAGE;
};

// The bulk injury routes return 400 with a normal body when the file has row errors.
// validateStatus tells axios to accept 200 and 400.
// Axios still rejects every other status. The caller then only handles real failures.
const postBulkInjuryFile = (url, file) => {
	const fd = new FormData();
	fd.append("file", file);
	return axios
		.post(url, fd, { validateStatus: (status) => status === 200 || status === 400 })
		.then((x) => x.data);
};

export class TrainingAPI {
	url =
		import.meta.env.MODE === "production"
			? "https://api.gpsdataviz.com/api"
			: "http://127.0.0.1/api";

	constructor() {
		axios.interceptors.request.use((config) => {
			if (config.url?.startsWith(this.url) && !config.headers["gpsdv-uuid"]?.trim()) {
				config.headers["gpsdv-uuid"] = crypto.randomUUID();
			}
			return config;
		});
	}

	getPlatformCharts = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/team-charts`, {})
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	savePlatformCharts = (payload) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/team-charts`, payload)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	getHelpVideoUrl = (key) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/help-center-video`, {
					params: { key },
				})
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	getDropdowns = ({ metricTypes, keyType = 'name' }) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/dropdown`, {
					params: {
						metric_types: metricTypes,
						key_type: keyType
					}
				})
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	getPlayerNotes = (playerId) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/playernotes`, {
					params: { player_id: playerId },
				})
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	performCustomAction = (payload = {}) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/admin/custom-action`, payload)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	addPlayerNote = (playerId, team, note) => {
		const data = { player_id: playerId, team_id: team, notes: note };
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/playernotes`, data)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	editPlayerNote = (notes, note_id) => {
		const data = { notes, note_id };
		return new Promise((resolve, reject) => {
			axios
				.patch(`${this.url}/playernotes`, data)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	deletePlayerNote = (note_id) => {
		return new Promise((resolve, reject) => {
			axios
				.delete(`${this.url}/playernotes`, { data: { note_id } })
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	getOverviewChartV2 = (params) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/dvLiteOverview`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	updateSettingsAutomaticActions = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/triggers`, data)
				.then((x) => resolve(x))
				.catch((e) => reject(e));
		});
	};

	getBullets = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/team-bullets`)
				.then((x) => resolve(x))
				.catch((e) => reject(e));
		});
	};

	updateBullets = (bullets) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/team-bullets`, { bullets })
				.then((x) => resolve(x))
				.catch((e) => reject(e));
		});
	};

	getSettingsAutomaticActions = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/triggers`)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	updateOverviewDefaultMetrics = (metrics) => {
		const data = { metrics, metric_type: "overview" };
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/teamkeystats`, data)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	getSingleStatTileData = (params) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/singleStatTileData`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	// One stat tile per request; the answer is that tile's answer.
	getStatTile = (body) => {
		return axios.post(`${this.url}/v2/stat-tiles`, body).then((x) => x.data);
	};

	getSettingsDatapointAvg = (params) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/v2/playerdata/override`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	createSettingsDatapoint = (payload) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/v2/playerdata/override`, payload)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	deleteSettingsDataPoint = (payload) => {
		return new Promise((resolve, reject) => {
			axios
				.delete(`${this.url}/v2/playerdata/override`, { data: payload })
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	getTeamList = async (params = {}) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/v2/teams`, { params })
				.then((x) => resolve({ data: x.data, status: x.status }))
				.catch((e) => reject(e));
		});
	};

	sendWelcomeEmail = async (player_id) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/send-welcome-email`, { player_id })
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	sendWelcomeEmailCoach = async (coachEmail) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/send-welcome-email`, { email: coachEmail, user_type: "coach" })
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	deletePlayer = async (player_id) => {
		return new Promise((resolve, reject) => {
			axios
				.delete(`${this.url}/v2/player`, { data: { player_id } })
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	uploadPlayerCsv = async (file) => {
		return new Promise((resolve, reject) => {
			const formData = new FormData();
			formData.append("file", file);
			formData.append("bulk_add", true);
			axios
				.post(`${this.url}/v2/player`, formData, {
					headers: {
						"Content-Type": "multipart/form-data",
					},
				})
				.then((x) => resolve(x))
				.catch((e) => reject(e));
		});
	};

	getCalendarTags = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/calendar-tag`)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	createCalendarTag = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/calendar-tag`, data)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	updateCalendarTag = (id, data) => {
		return new Promise((resolve, reject) => {
			axios
				.patch(`${this.url}/calendar-tag/${id}`, data)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	deleteCalendarTag = (id) => {
		return new Promise((resolve, reject) => {
			axios
				.delete(`${this.url}/calendar-tag/${id}`)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	getCalendarDayTags = (startDate, endDate) => {
		return new Promise((resolve, reject) => {
			const params = {};
			if (startDate) params.start_date = startDate;
			if (endDate) params.end_date = endDate;
			axios
				.get(`${this.url}/calendar-day-tag`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	bulkUpdateCalendarDayTags = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/calendar-day-tag/bulk`, data)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	getCurrentUserData = () => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url.slice(
				0,
				this.url.length - 4
			)}/current_user/`;
			axios
				.get(tempUrl)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	};

	savePlayerOrdering = (data) => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url}/players_order`;
			let params = {
				player_mapping: data,
			};

			axios
				.post(tempUrl, params)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getCostOfDay = (date_string) => {
		return new Promise((resolve, reject) => {
			const params = {
				date: date_string,
			};
			axios
				.get(
					`${this.url.slice(
						0,
						this.url.length - 4
					)}/get_prediction_day_intensity/`,
					{ params }
				)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getDrillCostPerMin = (playerSelections = undefined, startDate = undefined, endDate = undefined, days = undefined) => {
		let params = {
			start_date: startDate,
			end_date: endDate,
			days: days,
			player_selections: playerSelections
		};

		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/drillcostpermin`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	deleteInjury = (injury_id) => {
		return new Promise((resolve, reject) => {
			let params = {
				injury_id: injury_id,
			};

			axios
				.delete(`${this.url}/injury`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getRestDays = () => {
		return new Promise((resolve, reject) => {
			let params = {};

			axios
				.get(`${this.url}/restday`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	deleteRestDay = (rest_day_id) => {
		return new Promise((resolve, reject) => {
			let params = {
				rest_day_id: rest_day_id,
			};

			axios
				.delete(`${this.url}/restday`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	addRestDay = (date) => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url}/restday`;
			let params = {
				date: date,
			};

			axios
				.post(tempUrl, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	storeS3 = (
		playertek_file,
		playertek_line,
		FB_file,
		FB_line,
		testing_file,
		testing_line
	) => {
		return new Promise((resolve, reject) => {
			var formData = new FormData();

			formData.append("playertek_line", JSON.stringify(playertek_line));
			formData.append("playertek", playertek_file);

			formData.append("testing_line", JSON.stringify(testing_line));
			formData.append("testing", testing_file);

			formData.append("FB_line", JSON.stringify(FB_line));
			formData.append("FB", FB_file);

			axios
				.post(`${this.url}/stores3`, formData, {
					"Content-Type": "multipart/form-data",
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	storeS3V2 = (
		file_type,
		file
	) => {
		return new Promise((resolve, reject) => {
			var formData = new FormData();
			formData.append("file", file);
			formData.append("file_type", file_type);

			axios
				.post(`${this.url}/v2/stores3`, formData, {
					"Content-Type": "multipart/form-data",
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	storeAnnotatedPdfS3 = (file) => {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("file_type", "annotated-pdf");
		formData.append("bucket_name", "annotated-file-store");

		return axios.post(`${this.url}/v2/stores3`, formData).then((x) => x.data);
	};

	getWeeklyRestDay = () => {
		return new Promise((resolve, reject) => {
			let params = {};

			axios
				.get(`${this.url}/wrestday`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	updateWeeklyRestDay = (date) => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url}/wrestday`;
			let params = {
				date: date,
			};

			axios
				.post(tempUrl, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	wellnessEmail = (date, emails) => {
		return new Promise((resolve, reject) => {
			let params = {
				date: date,
				emails: emails,
			};

			axios
				.post(`${this.url}/email/wellness`, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	sendWellnessNotification = (date) => {
		return new Promise(async (resolve, reject) => {
			let params = {
				date: date,
			};

			try {
				const response = await axios.get(
					`${this.url}/missingresponses`,
					{
						params,
					}
				);
				resolve(response.data);
			} catch (error) {
				reject(error);
			}
		});
	};

	sendRPENotification = (date) => {
		return new Promise(async (resolve, reject) => {
			let params = {
				date: date,
			};

			try {
				const response = await axios.get(`${this.url}/missingrpe`, {
					params,
				});
				resolve(response.data);
			} catch (error) {
				reject(error);
			}
		});
	};

	surveyreponsecount = (date) => {
		return new Promise((resolve, reject) => {
			let params = {
				date: date,
			};

			axios
				.get(`${this.url}/surveyresponsecount`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getTeamSurvey = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/survey`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	setWellness = (questions, deleteLast = false) => {
		if (!questions.length && !deleteLast) {
			return Promise.resolve({ message: "No questions provided." });
		}
		let question_list = questions.map((question, index) => {
			return { question_id: question.questionId, order: index + 1 };
		});
		let params = {
			bulk: true,
			question_list,
			delete_last: deleteLast
		};
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/survey`, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getTeamInfo = () => {
		return new Promise((resolve, reject) => {
			let host = this.url;
			if (host.endsWith("/api")) {
				host = host.substring(0, host.lastIndexOf("/api"));
			}
			axios
				.get(`${host}/team-name/`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getTeamSessions = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/v2/team/sessions`)
				.then((x) => resolve(x.data?.sessions ?? []))
				.catch(() => {
					reject();
				});
		});
	};

	emailDistanceReport = (
		start_date,
		end_date,
		emails,
		mode,
		position = undefined,
		player_group = undefined
	) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date,
				end_date,
				emails,
				mode,
				position,
				player_group

			};
			const start = Date.now();
			axios
				.post(`${this.url}/distance_breakdown_report`, params, {
					timeout: 180000,
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					const end = Date.now();
					console.log("Time elapsed", end - start);

					if (
						end - start > 59000 && 
						(
							e?.message === "Network Error" ||
							e?.message?.toLowerCase()?.includes("timeout") ||
							e?.code === "ECONNABORTED"
						) && 
						e?.response === undefined
					) {
						const custom_err = "The report will be sent shortly."
						reject(custom_err);
					}

					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	emailACWRAnalysisReport = (
		date,
		emails,
		position = undefined,
		player_group = undefined,
		session = undefined
	) => {
		return new Promise((resolve, reject) => {
			let params = {
				date,
				emails,
				position,
				player_group,
				session
			};
			const start = Date.now();
			axios
				.post(`${this.url}/acwr_analysis_report`, params, {
					timeout: 180000,
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					const end = Date.now();
					console.log("Time elapsed", end - start);

					if (
						end - start > 59000 && 
						(
							e?.message === "Network Error" ||
							e?.message?.toLowerCase()?.includes("timeout") ||
							e?.code === "ECONNABORTED"
						) && 
						e?.response === undefined
					) {
						const custom_err = "The report will be sent shortly."
						reject(custom_err);
					}

					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	emailPrePostBodyWeightReport = (
		date,
		position = undefined,
		player_group = undefined
	) => {
		return new Promise((resolve, reject) => {
			let params = {
				date,
				position,
				player_group
			};
			const start = Date.now();
			axios
				.post(
					`${this.url}/pre-post-bw-report`,
					{ ...params },
					{
						responseType: "blob",
						validateStatus: function (status) {
							return status >= 200 && status < 300;
						},
					}
				)
				.then((response) => {
					resolve(response);
				})
				.catch(async (e) => {
					const end = Date.now();
					console.log("Time elapsed", end - start);

					if (end - start > 59000 && e?.message === "Network Error" && e?.response === undefined) {
						const custom_err = "The report will be sent shortly."
						reject(custom_err);
					}

					if (e?.response?.status === 422) {
						reject("No Data Found")
					}

					reject(await readErrorMessage(e?.response?.data));
				});
		});
	};

	emailForcePlateReport = ({
		date,
		agg,
		comparison,
		metrics,
		emails,
		position = undefined,
		player_group = undefined,
		group_by_position = false,
		sessions,
		start_date
	}) => {
		return new Promise((resolve, reject) => {
			let params = {
				date,
				agg,
				comparison,
				metrics,
				emails,
				position,
				player_group,
				group_by_position,
				sessions,
				start_date
			};
			const start = Date.now();
			axios
				// v2 builds the same report with the report fixes. The v1 endpoint stays for rollback.
				.post(`${this.url}/v2/forceplatereport`, params, {
					timeout: 180000,
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					const end = Date.now();
					console.log("Time elapsed", end - start, e);

					if (
						end - start > 59000 && 
						(
							e?.message === "Network Error" ||
							e?.message?.toLowerCase()?.includes("timeout") ||
							e?.code === "ECONNABORTED"
						) && 
						e?.response === undefined
					) {
						const custom_err = "The report will be sent shortly."
						reject(custom_err);
					}

					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	emailTestingReport = ({
		date,
		agg,
		comparison,
		metrics,
		emails,
		position = undefined,
		player_group = undefined,
		group_by_position = undefined,
		sessions,
		metricsAgg = undefined,
		startDate = undefined
	}) => {
		return new Promise((resolve, reject) => {
			let params = {
				date,
				agg,
				comparison,
				metrics,
				emails,
				position,
				player_group,
				group_by_position,
				sessions,
				metric_aggs: metricsAgg,
				start_date: startDate
			};
			const start = Date.now();
			axios
				// v2 builds the same report with the report fixes. The v1 endpoint stays for rollback.
				.post(`${this.url}/v2/testingreport`, params, {
					timeout: 180000,
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					const end = Date.now();
					console.log("Time elapsed", end - start);

					if (
						end - start > 59000 && 
						(
							e?.message === "Network Error" ||
							e?.message?.toLowerCase()?.includes("timeout") ||
							e?.code === "ECONNABORTED"
						) && 
						e?.response === undefined
					) {
						const custom_err = "The report will be sent shortly."
						reject(custom_err);
					}

					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	emailGPSReport = (date, emails, session, player_group, position, coaches_report) => {
		return new Promise((resolve, reject) => {
			let params = {
				date,
				emails,
				session,
				player_group,
				position,
				coaches_report
			};
			const start = Date.now();
			axios
				// v2 builds the same report with the report fixes. The v1 endpoint stays for rollback.
				.post(`${this.url}/v2/gpsreport`, params, {
					params,
					timeout: 180000,
				})
				.then((x) => {
					resolve(x.data)
				})
				.catch((e) => {
					if (e?.response?.status === 422) {
						resolve(e?.response?.data || "No data found");
					}
					const end = Date.now();
					console.log("Time elapsed", end - start);
					if (
						end - start > 59000 && 
						(
							e?.message === "Network Error" ||
							e?.message?.toLowerCase()?.includes("timeout") ||
							e?.code === "ECONNABORTED"
						) && 
						e?.response === undefined
					) {
						const custom_err = "The report will be sent shortly."
						reject(custom_err);
					}

					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	emailIndividualPlayerReport = (player_id, start_date, end_date, emails, report_types, metrics) => {
		return new Promise((resolve, reject) => {
			let params = {
				player_id,
				start_date,
				end_date,
				emails,
				report_types,
				metrics
			};
			const start = Date.now();
			axios
				.post(`${this.url}/player-report`, params, {
					params,
					timeout: 180000,
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					const end = Date.now();
					console.log("Time elapsed", end - start);
					if (
						end - start > 59000 && 
						(
							e?.message === "Network Error" ||
							e?.message?.toLowerCase()?.includes("timeout") ||
							e?.code === "ECONNABORTED"
						) && 
						e?.response === undefined
					) {
						const custom_err = "The report will be sent shortly."
						reject(custom_err);
					}

					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	emailBulkReportCards = (start_date, end_date, emails) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date,
				end_date,
				emails,
			};
			const start = Date.now();
			axios
				.post(`${this.url}/bulk-report-cards`, params, {
					params,
					timeout: 180000,
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					const end = Date.now();
					console.log("Time elapsed", end - start);
					if (
						end - start > 59000 && 
						(
							e?.message === "Network Error" ||
							e?.message?.toLowerCase()?.includes("timeout") ||
							e?.code === "ECONNABORTED"
						) && 
						e?.response === undefined
					) {
						const custom_err = "The report will be sent shortly."
						reject(custom_err);
					}

					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	addPracticePlan = (data, date, metadata = {}) => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url}/practiceplan`;
			let params = {
				data: data,
				date: date,
				metadata: metadata
			};

			axios
				.post(tempUrl, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	getPracticePlan = (start_date, end_date, player_id = undefined) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date,
				end_date,
			};
			if (player_id) params["player"] = player_id;

			axios
				.get(`${this.url}/practiceplan`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	addGame = (opponent, date, home, bulk = false, file = null) => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url}/game`;

			// If csv file is provided, use FormData to upload
			if (bulk) {
				const formData = new FormData();
				formData.append("file", file);
				formData.append("bulk", bulk);

				axios
					.post(tempUrl, formData, {
						headers: {
							"Content-Type": "multipart/form-data",
						},
					})
					.then((x) => resolve(x.data))
					.catch((e) => {
						reject(e);
					});
				return;
			}

			// Adding a single game
			let params = {
				opponent: opponent,
				date: date,
				home: home,
				bulk: bulk,
				team_score: null,
				opponent_score: null,
			};

			axios
				.post(tempUrl, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	deleteGame = (game_id) => {
		return new Promise((resolve, reject) => {
			let params = {
				game_id: game_id,
			};

			axios
				.delete(`${this.url}/game`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	addDrill = (name) => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url}/drills`;
			const teamId = localStorage.getItem("team-id");
			let params = {
				name: name,
				"team-id": teamId,
			};

			axios
				.post(tempUrl, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getDrillData = (startDate, endDate, drills) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date: startDate,
				end_date: endDate,
				drills: drills.toString(),
			};

			axios
				.get(`${this.url}/drilldata`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getDrills = (start_date = null, end_date = null, all = true) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date: start_date,
				end_date: end_date,
				all: all,
			};

			axios
				.get(`${this.url}/drills`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getDataTypesForReport = (date) => {
		return new Promise((resolve, reject) => {
			let params = {
				date,
			};

			axios
				.get(`${this.url}/dailyreport/datatypes`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	mergeDrills = (to_remove, to_assign) => {
		return new Promise((resolve, reject) => {
			let params = {
				to_remove: to_remove,
				assign_to: to_assign,
			};
			axios
				.post(`${this.url}/mergedrills`, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	getAllQuestions = () => {
		return new Promise((resolve, reject) => {
			let params = {
				with_answers: true,
			};
			axios
				.get(`${this.url}/questions`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getAllGames = () => {
		return new Promise((resolve, reject) => {
			let params = {
				all: true,
			};

			axios
				.get(`${this.url}/game`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getBreakdownTrainings = (
		date,
		selected_position,
		selected_player_groups,
		session_filter,
		drills,
		drilldown
	) => {

		return new Promise((resolve, reject) => {
			let params = {
				date: date,
				position: selected_position,
				player_groups: selected_player_groups,
				session: session_filter,
				drills,
				drilldown
			};

			axios
				.get(`${this.url}/v2/breakdown`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getTeamACWR = (params) => {
		return new Promise((resolve, reject) => {
			const teamId = localStorage.getItem("team-id");
			axios
				.get(`${this.url}/teamacwr`, {
					params: { ...params, "team-id": teamId },
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.log(e);
					reject();
				});
		});
	};

	emailBreakdownChart = (
		date,
		selected_positions,
		selected_player_groups,
		selected_session,
		selected_drills,
		selected_emails
	) => {

		return new Promise((resolve, reject) => {
			let params = {
				date: date,
				positions: selected_positions,
				player_groups: selected_player_groups,
				session: selected_session,
				drills: selected_drills,
				emails: selected_emails
			};

			axios
				.post(`${this.url}/email/breakdown`, params)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	exportPDFBreakdownChart = (
		date,
		selected_positions,
		selected_player_groups,
		selected_session,
		selected_drills
	) => {

		return new Promise((resolve, reject) => {
			let params = {
				date: date,
				positions: selected_positions,
				player_groups: selected_player_groups,
				session: selected_session,
				drills: selected_drills
			};

			axios
				.post(`${this.url}/export-pdf/breakdown`, params, { responseType: "blob" })
				.then((x) => resolve(x.data))
				.catch(async (e) => {
					reject(await readErrorMessage(e?.response?.data));
				});
		});
	};

	getPlayerCharts = (
		player_id,
		start_date,
		end_date,
		weekly,
		session,
		show_predictions
	) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date: start_date,
				end_date: end_date,
				player_id,
				weekly: weekly,
				session: session,
				show_predictions: show_predictions,
			};

			axios
				.get(`${this.url}/v2/playerchart`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					//console.error(e);
					reject(e);
				});
		});
	};

	emailPlayerCharts = (name, start_date, end_date, weekly) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date: start_date,
				end_date: end_date,
				name: name,
				weekly: weekly,
			};

			axios
				.get(`${this.url}/email/playerchart`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getTrainings = ({
		start_date,
		end_date,
		weekly,
		pos_filter,
		player_group = undefined,
		session_filter = undefined,
		show_predictions = false,
	}) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date: start_date,
				end_date: end_date,
				weekly: weekly,
				position: pos_filter,
				player_group: player_group,
				session: session_filter,
				show_predictions: show_predictions,
			};

			axios
				.get(`${this.url}/v2/trainings`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	emailOverviewCharts = (start_date, end_date, weekly) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date: start_date,
				end_date: end_date,
				weekly: weekly,
			};

			axios
				.get(`${this.url}/email/overview`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getBaselineOverview = () => {
		return new Promise((resolve, reject) => {
			let params = {};

			axios
				.get(`${this.url}/baselineoverview`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getTestingData = ({
		start_date,
		end_date,
		metric_type,
		is_movement_sig,
		pos_filter,
		stats,
		metric_id,
		player_id,
		player_group,
		sessions,
		metrics = [],
		multi_metrics = "",
		do_not_aggregate = false,
		skip_color = false,
	}) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date: start_date,
				end_date: end_date,
				metric_type,
				is_movement_sig,
				player_id,
				position: pos_filter,
				stats,
				metric_id,
				player_group,
				sessions,
				metrics,
				multi_metrics,
				do_not_aggregate,
				skip_color,
			};

			axios
				.get(`${this.url}/testingdata`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getDefaultTestingStats = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/testingkeystats`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getDefaultStats = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/teamkeystats`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	validateTeam = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/team-validation`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	updateDefaultTestingStats = (metrics) => {
		return new Promise((resolve, reject) => {
			let params = {
				metrics: metrics,
			};
			axios
				.post(`${this.url}/testingkeystats`, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	updateDefaultForcePlateStats = (metrics) => {
		return new Promise((resolve, reject) => {
			let params = {
				metrics: metrics,
				metric_type: "forceplate",
			};
			axios
				.post(`${this.url}/teamkeystats`, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	updateDefaultStats = (metrics, metricType) => {
		return new Promise((resolve, reject) => {
			let params = {
				metrics: metrics,
				metric_type: metricType,
			};
			axios
				.post(`${this.url}/teamkeystats`, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getRadarData = ({ start_date, end_date, metrics, name, pos_filter, player_group, sessions }) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date: start_date,
				end_date: end_date,
				metrics: metrics,
				name: name,
				position: pos_filter,
				player_group,
				sessions
			};

			axios
				.get(`${this.url}/testingradar`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	addTrainingV2 = (
		params,
		uuid
	) => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url}/upload-data`;
			const start = Date.now();
			axios
				.post(tempUrl, { ...params }, { headers: { 'gpsdv-uuid': uuid } })
				.then((x) => resolve(x.data))
				.catch((e) => {
					const end = Date.now();
					console.log("Time elapsed", end - start);

					if (end - start > 59000 && e?.message === "Network Error" && e?.response === undefined) {
						const custom_err = "Trainings added. If you requested a report, it will be sent shortly."
						reject(custom_err);
					}
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	addTraining = (
		FB,
		testing,
		playerTrek,
		drills,
		lift_type,
		send_email,
		start_date,
		end_date,
		fp_start_date,
		fp_end_date,
		custom_date,
		testing_start_date,
		testing_end_date,
		testing_data_provider,
		data_upload_action,
		emails,
		outliers
	) => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url}/trainings`;
			let params = {
				FB: FB,
				testing: testing,
				playerTrek: playerTrek,
				drills: drills,
				lift_type: lift_type,
				send_email: send_email,
				start_date: start_date,
				end_date: end_date,
				fp_start_date: fp_start_date,
				fp_end_date: fp_end_date,
				custom_date: custom_date,
				testing_start_date: testing_start_date,
				testing_end_date: testing_end_date,
				testing_data_provider: testing_data_provider,
				data_action: data_upload_action,
				emails: emails,
				outliers: outliers
			};
			const start = Date.now();
			axios
				.post(tempUrl, { ...params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					const end = Date.now();
					console.log("Time elapsed", end - start);

					if (end - start > 59000 && e?.message === "Network Error" && e?.response === undefined) {
						const custom_err = "Trainings added. If you requested a report, it will be sent shortly."
						reject(custom_err);
					}
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	getYearTrainingsV3 = (start_date, end_date, player_ids=[], positions=[], player_groups=[], player_statuses=[], sessions=[]) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date: start_date,
				end_date: end_date,
				player_ids,
				positions,
				player_groups,
				player_statuses,
				sessions
			};
			axios
				.get(`${this.url}/v3/year`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.log(e);
					reject();
				});
		});
	};

	// The month/day pair splits getYearTrainingsV3's single payload: /calendar/month
	// returns only the slim per-cell grid (server-resolved colour hex + fill) and
	// /calendar/day returns one date's per-metric detail. Same filter contract as
	// /v3/year so the two views stay interchangeable.
	getCalendarMonth = (start_date, end_date, player_ids=[], positions=[], player_groups=[], player_statuses=[], sessions=[], flag_types=[]) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date,
				end_date,
				player_ids,
				positions,
				player_groups,
				player_statuses,
				sessions,
				flag_types
			};
			axios
				.get(`${this.url}/calendar/month`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e);
				});
		});
	};

	getCalendarDay = (date, player_ids=[], positions=[], player_groups=[], player_statuses=[], sessions=[], flag_types=[]) => {
		return new Promise((resolve, reject) => {
			let params = {
				date,
				player_ids,
				positions,
				player_groups,
				player_statuses,
				sessions,
				flag_types
			};
			axios
				.get(`${this.url}/calendar/day`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e);
				});
		});
	};

	getFpAndTestingBreakdownTable = (
		start_date,
		end_date,
		metricId,
		stat,
		position_filter = [],
		player_group = undefined,
		sessions = undefined
	) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date,
				end_date,
				metricId,
				position_filter,
				stat,
				player_group,
				sessions,
			};
			axios
				.get(`${this.url}/fpandtesting/breakdowntable`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getTestingDataTable = (
		start_date,
		end_date,
		multi_metrics_array = [],
		position_filter = [],
		player_group = undefined,
		sessions = undefined,
		player_id = undefined
	) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date,
				end_date,
				'multi_metrics': multi_metrics_array,
				position_filter,
				player_group,
				sessions,
				player_id,
			};
			axios
				.get(`${this.url}/testingdatatable`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	sendDailyReportFootball = ({ data_types_object, date, emails }) => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url}/dailyreport/football`;
			let params = {
				...data_types_object,
				date,
				emails,
			};
			axios
				.post(tempUrl, { ...params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	sendDailyReportOlympic = ({ data_types_object, date, emails }) => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url}/dailyreport/olympic`;
			let params = {
				...data_types_object,
				date,
				emails,
			};
			axios
				.post(tempUrl, { ...params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	getDrillBreakdownIndividual = (drill, date, position) => {
		return new Promise((resolve, reject) => {
			let params = {
				drill: drill,
				date: date,
				position: position,
			};

			axios
				.get(`${this.url}/v2/drillbreakdown`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getDrillCompare = () => {
		return new Promise((resolve, reject) => {
			let params = {};

			axios
				.get(`${this.url}/drillcompare`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getDrillBreakdownTeam = (data) => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url}/v2/drillbreakdown`;
			let body = {
				pairs: data,
			};

			axios
				.post(tempUrl, body, {
					headers: {
						"Content-Type": "application/json",
					},
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					alert(e?.response?.data);
					reject();
				});
		});
	};

	getDrillDates = (drill) => {
		return new Promise((resolve, reject) => {
			let params = {
				drill: drill,
			};

			axios
				.get(`${this.url}/drilldates`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getBaseline = (position) => {
		return new Promise((resolve, reject) => {
			let params = {
				position: position,
			};

			axios
				.get(`${this.url}/baseline`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	getInjuries = (filters = {}) => {
		return new Promise((resolve, reject) => {
			// Build URLSearchParams to properly handle array parameters for Django's getlist()
			const params = new URLSearchParams();

			// Add main filter parameters if provided
			if (filters.start_date) params.append('start_date', filters.start_date);
			if (filters.end_date) params.append('end_date', filters.end_date);
			if (filters.player) params.append('player', filters.player);

			// Handle array parameters properly for Django's getlist()
			if (filters.positions && filters.positions.length > 0) {
				filters.positions.forEach(position => {
					params.append('positions', position);
				});
			}
			if (filters.groups && filters.groups.length > 0) {
				filters.groups.forEach(group => {
					params.append('groups', group);
				});
			}

			// Support legacy injury-specific filters for table column filtering
			if (filters.injury_types && filters.injury_types.length > 0) {
				filters.injury_types.forEach(type => {
					params.append('injury_types', type);
				});
			}
			if (filters.injury_locations && filters.injury_locations.length > 0) {
				filters.injury_locations.forEach(location => {
					params.append('injury_locations', location);
				});
			}
			if (filters.injury_status && filters.injury_status.length > 0) {
				filters.injury_status.forEach(status => {
					params.append('injury_status', status);
				});
			}

			axios
				.get(`${this.url}/injury?${params.toString()}`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	updateInjury = (data) => {
		return new Promise((resolve, reject) => {
			let params = {
				data: data,
			};
			axios
				.post(`${this.url}/injury`, params)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	newInjury = (player, injury_location, injury_type, date) => {
		return new Promise((resolve, reject) => {
			let params = {
				player: player,
				injury_location: injury_location,
				injury_type: injury_type,
				injury_start_date: date,
			};
			axios
				.post(`${this.url}/newinjury`, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	newInjuryV2 = (payload) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/newinjury`, payload)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e);
				});
		});
	};

	validateBulkInjuries = (file) => postBulkInjuryFile(`${this.url}/injury/bulk/validate`, file);

	saveBulkInjuries = (file) => postBulkInjuryFile(`${this.url}/injury/bulk`, file);

	updateBaseline = ({baselines}) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/baseline`, {baselines})
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	updatePlayer = (
		player_id,
		player_number,
		player_position,
		player_top_speed,
		player_trimp_min,
		player_trimp,
		player_max_hr,
		player_bw,
	) => {
		return new Promise((resolve, reject) => {
			let data = {
				player_id,
				number: player_number,
				position: player_position,
				top_speed: player_top_speed,
				trimp_min: player_trimp_min,
				trimp: player_trimp,
				max_hr: player_max_hr,
				bodyweight_baseline: player_bw
			};

			axios
				.patch(`${this.url}/v2/player`, data)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e);
				});
		});
	};

	editPlayer = (params) => {
		return new Promise((resolve, reject) => {
			if (params.first_name && params.last_name)
				params.name = params.first_name + " " + params.last_name;
			axios
				.patch(`${this.url}/v2/player`, params)
				.then((x) => resolve(x.data))
				.catch((e) => {
					// The caller shows this expected validation error to the user. Do not send it to Datadog RUM.
					if (e.response?.data !== "This email is in use by another user") console.error(e);

					reject(e);
				});
		});
	};

	getPlayer = (first_name, last_name, player_id) => {
		return new Promise((resolve, reject) => {
			let params = {
				first_name: first_name,
				last_name: last_name,
				player_id,
			};

			axios
				.get(`${this.url}/v2/player`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e);
				});
		});
	};

	getPlayerGroups = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/player_groups`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	createPlayerGroup = (name) => {
		return new Promise((resolve, reject) => {
			let params = {
				name,
			};

			axios
				.post(`${this.url}/player_groups`, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);

					reject();
				});
		});
	};

	editPlayerGroup = (
		name,
		new_name = undefined,
		included_in_report = undefined
	) => {
		return new Promise((resolve, reject) => {
			let params = {
				name,
				new_name,
				included_in_report,
			};

			axios
				.post(`${this.url}/player_groups`, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);

					reject();
				});
		});
	};

	deletePlayerGroup = (name) => {
		return new Promise((resolve, reject) => {
			let params = {
				name,
			};

			//delete not working. hacky workaround using put
			axios
				.put(`${this.url}/player_groups`, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					console.log(e, { params });

					reject();
				});
		});
	};

	updatePlayerGroupMapping = (player_id, name, action) => {
		return new Promise((resolve, reject) => {
			let params = {
				player_id,
				name,
				action,
			};

			axios
				.post(`${this.url}/update_player_groups_mapping`, params, {
					params,
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getTeamMetrics = (
		include_wellness = false,
		include_testing = false,
		include_forceplate = false,
		include_nutrition = false,
		include_cognition = false,
		individual = "true",
		include_gk_metrics= false,
		include_counts = false,
		include_helmet = false
	) => {
		return new Promise((resolve, reject) => {
			let params = {
				individual: individual,
				wellness: include_wellness.toString(),
				testing: include_testing.toString(),
				forceplate: include_forceplate.toString(),
				nutrition: include_nutrition.toString(),
				cognition: include_cognition.toString(),
				include_gk_metrics: include_gk_metrics.toString(),
				counts: include_counts.toString(),
				helmet: include_helmet.toString()
			};

			axios
				.get(`${this.url}/assignmetrics`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	/**
	 * The include-list options a count metric picker renders, as
	 * { source: { facet: [{ value, label, color? }] } }.
	 *
	 * Answers 404 while the backend count gate is closed, which the caller reads as
	 * "no options yet" rather than an error.
	 */
	getCountOptions = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/sandbox/count-options`, {})
				.then((x) => resolve(x.data))
				.catch((e) => {
					if (e?.response?.status === 404) {
						resolve(null);
						return;
					}
					console.error(e);
					reject(e);
				});
		});
	};

	getMetricOrder = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/metrics_order`, {})
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	saveMetricOrdering = (data) => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url}/metrics_update`;
			let params = {
				metric_mapping: data,
			};

			axios
				.post(tempUrl, params)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getBaselineChart = (
		date,
		sort_by,
		selected_position,
		selected_player_group
	) => {
		return new Promise((resolve, reject) => {
			let params = {
				date: date,
				sort_by: sort_by,
				position: selected_position,
				player_group: selected_player_group,
			};

			axios
				.get(`${this.url}/baselineChart`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	emailBaselineChart = (date, sort_by, selected_position) => {
		return new Promise((resolve, reject) => {
			let params = {
				date: date,
				sort_by: sort_by,
				position: selected_position,
			};

			axios
				.get(`${this.url}/email/baseline`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getAllPlayers = (retired) => {
		return new Promise((resolve, reject) => {
			let params = {
				retired: retired,
			};

			axios
				.get(`${this.url}/players`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					// console.error(e);
					reject();
				});
		});
	};

	// Loads a permissioned headshot (the headshot_image_url ref from /api/players).
	// axios carries the shared auth + gpsdv-team-id headers (both live on
	// axios.defaults.headers.common) and follows the 302 to S3 transparently;
	// responseType blob → the image bytes. 404/403/error → null ("no image").
	getHeadshotBlob = (ref) => {
		const apiOrigin = this.url.replace(/\/api$/, "");
		return axios
			.get(`${apiOrigin}${ref}`, { responseType: "blob" })
			.then((x) => x.data)
			.catch(() => null);
	};

	// Uploads a player headshot (PNG, ≤5MB — validated client-side before calling).
	// A FormData body lets axios/the browser set multipart/form-data WITH the
	// boundary, so we must NOT set Content-Type manually (unlike the older upload
	// methods above). Auth rides axios.defaults.headers.common. Errors propagate so
	// the thunk can surface them. Returns the new headshot_image_url ref (bumped ?v).
	uploadHeadshot = (playerId, file) => {
		const fd = new FormData();
		fd.append("file", file, "headshot.png");
		return axios
			.post(`${this.url}/images/headshots/${playerId}`, fd)
			.then((x) => x.data.headshot_image_url);
	};

	getAllPlayerStatus = (retired = false, start_date = undefined, n_day = 5) => {
		return new Promise((resolve, reject) => {
			let params = {
				retired: retired,
				n_day,
				start_date
			};

			axios
				.get(`${this.url}/all-player-status`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					// console.error(e);
					reject();
				});
		});
	}

	getPlayerMetrics = (start_date, end_date, acwr = false) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date: start_date,
				end_date: end_date,
				acwr: acwr,
			};

			axios
				.get(`${this.url}/playermetrics`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getPositions = (get_all = undefined) => {
		return new Promise((resolve, reject) => {
			let params = {
				get_all,
			};

			axios
				.get(`${this.url}/positions`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	compareSessions = (
		game_compare,
		num_games,
		date1,
		date2,
		date3,
		date4,
		date5,
		pos_filter,
		player_groups_filter
	) => {
		return new Promise((resolve, reject) => {
			let params = {
				game_compare: game_compare,
				position: pos_filter,
				player_group: player_groups_filter,
			};
			if (game_compare) {
				params["num_games"] = num_games;
			} else {
				params["date1"] = date1;
				params["date2"] = date2;
				params["date3"] = date3;
				params["date4"] = date4;
				params["date5"] = date5;
			}

			axios
				.get(`${this.url}/compare`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	emailComparisonData = (
		game_compare,
		num_games,
		date1,
		date2,
		date3,
		date4,
		date5,
		pos_filter
	) => {
		return new Promise((resolve, reject) => {
			let params = {
				game_compare: game_compare,
				position: pos_filter,
			};
			if (game_compare) {
				params["num_games"] = num_games;
			} else {
				params["date1"] = date1;
				params["date2"] = date2;
				params["date3"] = date3;
				params["date4"] = date4;
				params["date5"] = date5;
			}

			axios
				.get(`${this.url}/email/compare`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	onBoardRoster = (data) => {
		return new Promise((resolve, reject) => {
			let tempUrl = `${this.url}/onboard/roster`;
			let params = {
				data: data,
			};

			axios
				.post(tempUrl, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	getUnrecognizedNames = () => {
		return new Promise((resolve, reject) => {
			let params = {};

			axios
				.get(`${this.url}/unrecognizednames`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getAlternateNames = () => {
		return new Promise((resolve, reject) => {
			let params = {};

			axios
				.get(`${this.url}/nickname`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	addAlternateName = (nickname, player_id) => {
		return new Promise((resolve, reject) => {
			let params = {
				nickname,
				player_id,
				official_name: " ",
			};
			axios
				.post(`${this.url}/nickname`, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	deleteAlternateName = (alternate_name_id) => {
		return new Promise((resolve, reject) => {
			let params = {
				alternate_name_id: alternate_name_id,
			};
			let config = { params };
			config.params = params;
			axios
				.delete(`${this.url}/nickname`, config)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getTiles = (time_period = "today", date) => {
		return new Promise((resolve, reject) => {
			let params = {
				time_period,
			};
			if (date) {
				params.date = String(date);
			}

			axios
				.get(`${this.url}/tiles`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	saveDashboard = (layouts, dashItems) => {
		return new Promise((resolve, reject) => {
			let params = {
				layouts: layouts,
				dashItems: dashItems,
			};
			axios
				.post(`${this.url}/dashboard`, params, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getDashboard = (start_date, end_date) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date: start_date,
				end_date: end_date,
			};

			axios
				.get(`${this.url}/dashboard`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getDashboardTileData = (data, startDate, endDate) => {
		return new Promise((resolve, reject) => {
			let params = {
				data: JSON.stringify(data),
				startDate: startDate,
				endDate: endDate,
			};

			axios
				.get(`${this.url}/dashboardtile`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getWellnessTeams = () => {
		return new Promise((resolve, reject) => {
			let host = this.url;
			if (host.endsWith("/api")) {
				host = host.substring(0, host.lastIndexOf("/api"));
			}
			axios
				.get(`${host}/team-has-wellness/`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};
	addPlayers = (players) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/v2/player`, players)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	addCoach = (coach) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/coach-onboarding`, coach)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	editCoach = (coach) => {
		return new Promise((resolve, reject) => {
			axios
				.patch(`${this.url}/coach-onboarding`, coach)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	deleteCoach = (coachEmail) => {
		return new Promise((resolve, reject) => {
			axios
				.request({
					url: `${this.url}/coach-onboarding`,
					method: 'DELETE',
					data: { email: coachEmail }
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	getLeaderBoard = (
		start_date,
		end_date,
		position = undefined,
		player_groups = undefined,
		limit = 25,
		group_by = undefined
	) => {
		return new Promise((resolve, reject) => {
			let params = {
				start_date: start_date,
				end_date: end_date,
				position,
				player_groups,
				limit,
				group_by
			};

			axios
				.get(`${this.url}/leaderboard`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getLeaderBoardPDF = (
		start_date,
		end_date,
		agg,
		position = undefined,
		player_groups = undefined,
		metrics = undefined,
		mode = undefined,
		limit = undefined,
		group_by = undefined,
		orientation = undefined
	) => {

		console.log(start_date, end_date, agg, position, player_groups, metrics, mode, limit, group_by, orientation);


		return new Promise((resolve, reject) => {
			const data = {
				start_date,
				end_date,
				agg,
				position,
				player_groups,
				metrics,
				mode,
				limit,
				group_by,
				orientation
			};

			console.log('Starting PDF request with data:', data);
			console.log('Request URL:', `${this.url}/leaderboardreport`);

			axios
				.post(
					`${this.url}/leaderboardreport`,
					{ ...data },
					{
						responseType: "blob",
						validateStatus: function (status) {
							console.log('Response status:', status);
							return status >= 200 && status < 300;
						},
					}
				)
				.then((response) => {
					resolve(response);
				})
				.catch(async (error) => {
					console.error('Request failed with error:', error);
					if (error.response) {
						reject(await readErrorMessage(error.response.data));
					} else if (error.request) {
						console.error('No response received:', error.request);
						reject('No response received from server');
					} else {
						console.error('Error setting up request:', error.message);
						reject(error.message);
					}
				});
		});
	};

	getBWReportPDF = (start_date, end_date, weekly) => {
		return new Promise((resolve, reject) => {
			const data = {
				start_date,
				end_date,
				weekly
			};

			axios
				.post(
					`${this.url}/bw_report`,
					{ ...data },
					{
						responseType: "blob",
						validateStatus: function (status) {
							return status >= 200 && status < 300;
						},
					}
				)
				.then((response) => {
					resolve(response);
				})
				.catch(async (error) => {
					reject(await readErrorMessage(error?.response?.data));
				});
		});
	};

	getDataDeletionTypesForDate = (params) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/v2/data-deletion`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	deleteDataForDate = (payload) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/v2/data-deletion`, { ...payload })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e.response)
				});
		});
	};

	getComparisonTypes = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/singleStatTileComparisons`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};
	getMiscellaneousConstants = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/misc-constants`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	updateDifficultyBarConfig = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/difficulty-bar-config`, data)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error("Error updating difficulty bar config: ", e);
					reject(e?.response?.data || "Error updating difficulty bar config");
				});
		});
	};

	updateAcwrThresholdConfig = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/acwr-threshold-config`, data)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error("Error updating ACWR threshold config: ", e);
					reject(e?.response?.data || "Error updating ACWR threshold config");
				});
		});
	};

	updateSessionDurationSource = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/session-duration-source`, data)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error("Error updating session duration source: ", e);
					reject(e?.response?.data || "Error updating session duration source");
				});
		});
	};

	updateGymMetricColorLogic = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.patch(`${this.url}/gym-metric-color-logic`, { ...data })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e?.response?.data || "Error updating forceplate/testing color logic");
				});
		});
	};

	getGymMetricColorLogicMatches = (logic) => {
		return new Promise((resolve, reject) => {
			const params = { logic };
			axios
				.get(`${this.url}/gym-metric-color-logic`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error("Error getting forceplate/testing color logic matches:", e);
					reject(e?.response?.data || "Error getting forceplate/testing color logic matches");
				});
		});
	};

	getScatterplotData = (payload) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/scatterplot`, { ...payload })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	getMultiRangeComparisonData = (payload) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/comparison/multi-range`, { ...payload })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	get1v1ComparisonData = (payload) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/comparison/vs`, { ...payload })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	deletePracticePlanByDate = (date) => {
		const params = { date };
		return new Promise((resolve, reject) => {
			axios
				.delete(`${this.url}/practiceplan`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	}

	// Day targets. Each call rejects with the axios
	// error, so a caller can show the server's message (for example a 409 conflict).
	_dayTargetRequest = (method, path, { params, data } = {}) =>
		axios({ method, url: `${this.url}/day-targets${path}`, params, data })
			.then((x) => x.data)
			.catch((e) => {
				console.error(e);
				throw e;
			});

	// -> { date, method: "practice_plan" | "fixed_values" | null, practice_plan, fixed_values }
	getDayTargetDay = (date) => this._dayTargetRequest("get", "/day", { params: { date } });

	// -> { updated_at, values: { [playerId]: { [key]: value } }, derived }; 404 when the day has none
	getDayTargetValues = (date) => this._dayTargetRequest("get", "", { params: { date } });

	// -> { date }: the last day with a day target of either method
	getDayTargetLatestDate = () => this._dayTargetRequest("get", "/latest-date");

	createDayTarget = (date, values) => this._dayTargetRequest("post", "", { data: { date, values } });

	updateDayTarget = (date, values) => this._dayTargetRequest("put", "", { data: { date, values } });

	deleteDayTarget = (date) => this._dayTargetRequest("delete", "", { params: { date } });

	// -> { source, weekday, values: { [playerId]: { [key]: value } }, fallback }
	getDayTargetReferenceValues = (date, source) =>
		this._dayTargetRequest("get", "/reference-values", { params: { date, source } });

	// scope: { player_id } | { position_id } | { scope: "team" }
	// -> { rows: [{ key, label, value }], weekdays: [{ date, label, value }], recommendation, player_count }
	getDayTargetSuggestions = (date, metric_key, scope) =>
		this._dayTargetRequest("get", "/suggestions", { params: { date, metric_key, ...scope } });

	// Chatbot Endpoints
	callChatbotApi = ({ inboundMessage, messages, conversationId, version = "0.1", streamingUuid }) => {
		return new Promise((resolve, reject) => {
			let payload = {
				inbound_message: inboundMessage,
				messages: messages,
				conversation_id: conversationId
			};

			const urlPrefix = {
				"0.1": "/chatbot",
				"1.0": "/chatbot/v1/chat",
			}[version] || "/chatbot"
			const url = `${this.url}${urlPrefix}`

			const headers = streamingUuid ? {
				'gpsdv-uuid': streamingUuid
			} : {}

			axios
				.post(url, { ...payload }, { headers: headers })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error("Error calling chatbot API: ", e);
					reject();
				});
		});
	}

	reviewAIMessage = (aiMessage, metadata, transcript, notes) => {
		return new Promise((resolve, reject) => {
			let payload = {
				input_sentence: aiMessage,
				metadata: metadata,
				transcript: transcript,
				user_notes: notes
			};
			axios
				.post(`${this.url}/chatbot/rag`, { ...payload })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error("Error calling chatbot API: ", e);
					reject();
				});
		});
	}

	getChatbotConversations = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/chatbot/conversation`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error("Error getting chatbot conversations: ", e);
					reject();
				});
		});
	}

	getChatbotActionMetadata = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/chatbot/action/edit`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error("Error getting chatbot function metadata: ", e);
					reject();
				});
		});
	}

	sendChatbotMessageWithAction = ({ inboundMessage, conversationId, streamingUuid, action }) => {
		return new Promise((resolve, reject) => {
			let payload = {
				inbound_message: inboundMessage,
				action: action,
				conversation_id: conversationId
			};
			const url = `${this.url}/chatbot/action/edit`

			const headers = streamingUuid ? {
				'gpsdv-uuid': streamingUuid
			} : {}

			axios
				.post(url, { ...payload }, { headers: headers })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error("Error calling chatbot API: ", e);
					reject();
				});
		});
	}

	getChatbotV2Conversations = ({ limit = 50, offset = 0 } = {}) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/chatbot/v2/conversations/`, { params: { limit, offset } })
				.then((x) => {
					// The sidebar pages by array length, so a non-array body must fail loudly here.
					if (!Array.isArray(x.data)) {
						const err = new Error("Chatbot V2 conversation list is not an array");
						Sentry.captureException(err);
						return reject(err);
					}
					resolve(x.data);
				})
				.catch((e) => {
					console.error("Error getting chatbot V2 conversations: ", e);
					reject(e);
				});
		});
	}

	getChatbotV2Conversation = (id) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/chatbot/v2/conversations/${id}/`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error("Error getting chatbot V2 conversation: ", e);
					reject(e);
				});
		});
	}

	postChatV2Stream = async ({ input, conversationId, models, images, replyTo, choice, choices }) => {
		const body = {
			input,
			conversation_id: conversationId ?? null,
			// This client draws the WhistleAgent parts (action_card, question, action_result).
			// Without the flag the backend sends only their text copies, which is what an older
			// web build or the mobile app can draw. Every chat request sends it.
			agent_parts: true,
			...(models ? { models } : {}),
			...(images?.length ? { images } : {}),
			// An answer to a WhistleAgent question. The server returns 400 for choice or choices
			// without reply_to, so the caller sets them together.
			...(replyTo ? { reply_to: replyTo } : {}),
			...(choice ? { choice } : {}),
			...(choices?.length ? { choices } : {}),
		};
		// fetch bypasses the axios interceptor that keeps Authorization fresh
		// after token expiry, so pull a current token; fall back to the axios
		// default header (the pre-expiry behavior) if the refresh fails.
		const token = await getFreshAccessToken().catch(() => null);
		return fetch(`${this.url}/chatbot/v2/chat`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...axios.defaults.headers.common,
				...(token ? { Authorization: token } : {}),
			},
			body: JSON.stringify(body),
		});
	}

	// Sandbox AI single-shot chat turn. Plain fetch (not axios 0.21) so the
	// caller's AbortController signal can cancel an in-flight turn.
	postSandboxAIChat = ({ dashboardId, input, conversationId, clientSpec, signal }) => {
		return fetch(`${this.url}/chatbot/v2/sandbox/chat`, {
			method: "POST",
			headers: { "Content-Type": "application/json", ...axios.defaults.headers.common },
			body: JSON.stringify({
				dashboard_id: dashboardId,
				input,
				conversation_id: conversationId ?? null,
				client_spec: clientSpec ?? null,
			}),
			signal,
		}).then(async (r) => {
			if (!r.ok) {
				const body = await r.json().catch(() => ({}));
				throw Object.assign(new Error(body.error || body.message || `HTTP ${r.status}`), { status: r.status });
			}
			return r.json();
		});
	};

	// Uploads one chatbot image to the shared chatbot-images endpoint. Client mints the uuid
	// (create-only; a fresh uuid per image). FormData lets axios set the multipart boundary — do NOT
	// set Content-Type. Auth rides axios.defaults.headers.common. Returns the immutable ref string.
	uploadChatbotImage = (file) => {
		const uuid = crypto.randomUUID();
		const fd = new FormData();
		fd.append("file", file);
		return axios
			.post(`${this.url}/images/chatbot-images/${uuid}`, fd)
			.then((x) => x.data.url);
	};

	// Resolves a chatbot-image ref to bytes. axios carries auth + follows the 302 to S3.
	// 403 (not the uploader) / 404 (deleted) / any error → null ("couldn't load").
	getChatbotImageBlob = (ref) => {
		const apiOrigin = this.url.replace(/\/api$/, "");
		return axios
			.get(`${apiOrigin}${ref}`, { responseType: "blob" })
			.then((x) => x.data)
			.catch(() => null);
	};

	updateTeamSettings = (data) => {
		return new Promise((resolve, reject) => {
			let payload = {
				...data
			};
			axios
				.patch(`${this.url}/team/settings`, { ...payload })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error("Error calling update settings API: ", e);
					reject();
				});
		});
	}

	updateTeamSettingsWithFile = (data, logoFile) => {
		return new Promise((resolve, reject) => {
			const formData = new FormData();
			
			// Append the logo file
			formData.append("team_logo", logoFile);
			
			// Append other settings as JSON string
			formData.append("settings_data", JSON.stringify(data));

			axios
				.patch(`${this.url}/team/settings`, formData, {
					headers: {
						"Content-Type": "multipart/form-data",
					},
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error("Error calling update settings API with file: ", e);
					reject();
				});
		});
	}

	getDrillDownChartData = ({
		date,
		position,
		player_group,
		sessions,
		drilldown_type,
		metric_id,
		player_id
	}) => {
		return new Promise((resolve, reject) => {
			let params = {
				date,
				position,
				player_group,
				sessions,
				drilldown_type,
				metric_id,
				player_id
			};

			axios
				.get(`${this.url}/drilldown-charts`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	}

	getPresets = (params) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/presets`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject();
				});
		});
	};

	createPreset = (data) => {
		return new Promise((resolve, reject) => {
			let params = {
				...data
			};
			axios
				.post(`${this.url}/presets`, { ...params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	updatePreset = (data) => {
		return new Promise((resolve, reject) => {
			let payload = {
				...data
			};
			axios
				.patch(`${this.url}/presets`, { ...payload })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	}

	deletePreset = (preset_id) => {
		return new Promise((resolve, reject) => {
			let params = {
				preset_id,
			};
			axios
				.delete(`${this.url}/presets`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getColorStrategies = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/color-strategies`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	createColorStrategy = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/color-strategies`, data)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	updateColorStrategy = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.patch(`${this.url}/color-strategies`, data)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	deleteColorStrategy = (id) => {
		return new Promise((resolve, reject) => {
			axios
				.delete(`${this.url}/color-strategies`, {
					params: { strategy_id: id }
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	getWeatherPresets = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/weather-presets`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	addWeatherPreset = (zipcode, name) => {
		return new Promise((resolve, reject) => {
			const data = {
				zipcode,
				name
			};
			axios
				.post(`${this.url}/weather-presets`, data)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	deleteWeatherPreset = (zipcode) => {
		return new Promise((resolve, reject) => {
			const params = {
				zipcode
			};
			axios
				.delete(`${this.url}/weather-presets`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	updateUserPermissions = (userId, permissions) => {
		return new Promise((resolve, reject) => {
			axios
				.patch(`${this.url}/create-user`, {
					user_id: userId,
					permissions
				})
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	getWeatherData = (zipcode, startDate, endDate) => {
		return new Promise((resolve, reject) => {
			let params = {
				zipcode,
				start_date: startDate,
			};

			if (endDate) {
				params.end_date = endDate;
			}

			axios
				.get(`${this.url}/weather`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	};

	getGraphicsPackage = () => {

		return new Promise((resolve, reject) => {
			axios
			.post(`${this.url}/graphics-package`, {}, { responseType: "blob" })
			.then((x) => {
				// Handle file download
				const blob = new Blob([x.data]);
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				
				// Extract filename from Content-Disposition header if available
				const contentDisposition = x.headers['content-disposition'];
				let filename = 'report.pdf';
				if (contentDisposition) {
					const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
					if (filenameMatch && filenameMatch[1]) {
						filename = filenameMatch[1];
					}
				}
				
				link.setAttribute('download', filename);
				document.body.appendChild(link);
				link.click();
				link.remove();
				window.URL.revokeObjectURL(url);
				resolve({ success: true, message: 'Report downloaded successfully' });
				
			})
			.catch(async (e) => {
				console.error('Error downloading graphics package:', e);
				reject(await readErrorMessage(e?.response?.data));
			});

		});
	}

	generateGeneralReport = (reportConfig) => {
		/**
		 * Unified method for generating all types of reports through the new general report endpoint.
		 * This consolidates the various disparate report methods into a single interface.
		 * 
		 * @param {Object} reportConfig - Configuration object for the report
		 * @param {string} reportConfig.report_type - Type of report to generate
		 * @param {string} [reportConfig.date] - Date for single date reports (YYYY-MM-DD)
		 * @param {Array<string>} [reportConfig.date_range] - Date range for range reports [start, end]
		 * @param {Array<string>} reportConfig.emails - Email recipients
		 * @param {Array<string>} [reportConfig.position_filter] - Position filters
		 * @param {Array<number>} [reportConfig.player_group_filter] - Player group IDs
		 * @param {number} [reportConfig.player_filter] - Single player ID for player reports
		 * @param {boolean} [reportConfig.group_by_position] - Whether to group by position
		 * @param {string} [reportConfig.comparison_type] - Comparison type for testing/forceplate
		 * @param {Array} [reportConfig.metrics] - Report-specific metrics
		 * @param {Array<string>} [reportConfig.player_report_data_streams] - Data streams for player reports
		 * 
		 * Supported report types:
		 * - 'gps': GPS Report
		 * - 'game_day_comparison': Gameday Comparison Report
		 * - 'coach-gps': Coach's Report (GPS)
		 * - 'forceplate': Force Plate Report
		 * - 'testing': Testing Report  
		 * - 'player_report': Individual Player Report
		 * - 'report-card-bulk': Team Report Cards - Bulk Export
		 * - 'daily_all_tech': Daily Report (All Tech)
		 * - 'single_week_distance_breakdown': Single Week Distance Breakdown
		 * - 'multi_week_distance_breakdown': Multi Week Distance Breakdown
		 * - 'body_weight_report': One Pager Body Weight Report
		 * - 'custom_breakdown_table': Custom Breakdown Table (Coming Soon)
		 */
		return new Promise((resolve, reject) => {
			if (!reportConfig.report_type) {
				reject("report_type is required");
				return;
			}

		// Only require emails if mode is 'email' (default) or not specified
		const mode = reportConfig.mode || 'email';
		if (mode === 'email' && (!reportConfig.emails || reportConfig.emails.length === 0)) {
			reject("At least one email recipient is required");
			return;
		}

		const start = Date.now();
		const payload = {
			...reportConfig
		};

		const axiosConfig = {
			timeout: 180000, // 3 minute timeout like other report methods
		};
      
		// If mode is 'pdf', we expect binary data (blob) instead of JSON
		if (mode === 'pdf') {
			axiosConfig.responseType = 'blob';
		}

		axios
			.post(`${this.url}/generate-report`, payload, axiosConfig)
			.then((x) => {
				if (mode === 'pdf') {
					// Handle file download
					const blob = new Blob([x.data]);
					const url = window.URL.createObjectURL(blob);
					const link = document.createElement('a');
					link.href = url;
					
					// Extract filename from Content-Disposition header if available
					const contentDisposition = x.headers['content-disposition'];
					let filename = 'report.pdf';
					if (contentDisposition) {
						const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
						if (filenameMatch && filenameMatch[1]) {
							filename = filenameMatch[1];
						}
					}
					
					// Caller wants the raw blob (e.g. to annotate) rather than download it.
					if (reportConfig.returnBlob) {
						window.URL.revokeObjectURL(url);
						resolve({ success: true, blob, filename });
						return;
					}
					link.setAttribute('download', filename);
					document.body.appendChild(link);
					link.click();
					link.remove();
					window.URL.revokeObjectURL(url);
					
					resolve({ success: true, message: 'Report downloaded successfully' });
				} else {
					// Email mode - return JSON response
					resolve(x.data);
				}
			})
		.catch(async (e) => {
			const end = Date.now();
			console.log("Time elapsed", end - start);

          // Handle timeout scenario like other report methods
				if (
					end - start > 59000 && 
					(
						e?.message === "Network Error" ||
						e?.message?.toLowerCase()?.includes("timeout") ||
						e?.code === "ECONNABORTED"
					) && 
					e?.response === undefined
				) {
					const custom_err = "The report will be sent shortly."
					reject(custom_err);
					return;
          }
			
			reject(await readErrorMessage(e?.response?.data));
		});
	});
	};

	addTreatmentLog = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/treatment-log`, data)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	}

	getAllTreatmentLogs = (params) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/treatment-log`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	}

	getTreatmentLog = (id) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/treatment-log/${id}`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	}

	deleteTreatmentLog = (id) => {
		return new Promise((resolve, reject) => {
			axios
				.delete(`${this.url}/treatment-log/${id}`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	}

	updateTreatmentLog = (id, data) => {
		return new Promise((resolve, reject) => {
			axios
				.patch(`${this.url}/treatment-log/${id}`, data)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	}

	getBulkTreatmentLogs = (params) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/treatment-log/bulk`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	}

	saveBulkTreatmentLogs = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/treatment-log/bulk`, data)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	}

	getActivityLog = (page, activity, userId, startDate, endDate, pageSize) => {
		if (startDate) {
			startDate = new Date(startDate)
		}

		if (endDate) {
			endDate = new Date(endDate)
		}

		const params = {
			page,
			page_size: pageSize,
			start_date: startDate ? new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 1).toISOString() : startDate,
			end_date: endDate ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1).toISOString() : endDate,
			user_ids: userId,
			activity: activity
		};

		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/activity-log`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				})
		})
	}

	getPermissionsForTeamUsers = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/platform-permissions`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e?.response?.data || DEFAULT_ERR_MESSAGE);
				});
		});
	}

	getStatusTrackerData = (date) => {
		return new Promise((resolve, reject) => {
			let params = {
				date,
			};
			axios
				.get(`${this.url}/status-tracker`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	getStatusTrackerFlagMetadata = (year, month) => {
		return new Promise((resolve, reject) => {
			let params = {
				year, month
			};
			axios
				.get(`${this.url}/status-tracker/flag-metadata`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject();
				});
		});
	};

	createCustomStatus = (data) => {
		return new Promise((resolve, reject) => {
			let params = {
				...data
			};
			axios
				.post(`${this.url}/status-tracker/status`, { ...params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	updateCustomStatus = (data) => {
		return new Promise((resolve, reject) => {
			let params = {
				...data
			};
			axios
				.patch(`${this.url}/status-tracker/status`, { ...params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	deleteCustomStatus = (data) => {
		return new Promise((resolve, reject) => {
			let params = {
				...data
			};
			axios
				.delete(`${this.url}/status-tracker/status`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	getAvailableStatuses = (start_date, end_date) => {
		const params = {
			start_date,
			end_date
		}
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/status-tracker/status`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	selectPlayerStatus = (data) => {
		return new Promise((resolve, reject) => {
			let params = {
				...data
			};
			axios
				.post(`${this.url}/status-tracker/select-status`, { ...params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	deletePlayerStatus = (data) => {
		return new Promise((resolve, reject) => {
			let params = {
				...data
			};
			axios
				.delete(`${this.url}/status-tracker/select-status`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	};

	baselineBuilder = (startDate, endDate, days, playerSelections) => {
		let params = {
			start_date: startDate,
			end_date: endDate,
			days: days,
			player_selections: playerSelections
		};
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/baseline-builder`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	postGpsMetricMappingDataSource = (mode, data) => {
		return new Promise((resolve, reject) => {
			let params = {
				mode,
				...data
			};
			axios
				.post(`${this.url}/sample-data-getter`, {...params})
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	getThirdPartyOnboarding = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/third-party-onboarding`, {})
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	postThirdPartyOnboarding = (data) => {
		return new Promise((resolve, reject) => {
			let params = {
				...data
			};

			axios
				.post(`${this.url}/third-party-onboarding`, { ...params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	validateFormula = (formula, sample_data) => {
		return new Promise((resolve, reject) => {
			let params = {
				formula,
				sample_data
			};
			axios
				.post(`${this.url}/validate_formula`, {...params})
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	patchThirdPartyOnboarding = (data) => {
		return new Promise((resolve, reject) => {
			let params = {
				...data
			};

			axios
				.patch(`${this.url}/third-party-onboarding`, { ...params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	postValidateGpsMetricsMapping = (metricsMappings, dateFormat = [], dataSource = "api") => {
		return new Promise((resolve, reject) => {
			let params = {
				metric_mappings: metricsMappings,
				date_format: dateFormat,
				data_source: dataSource
			};
			axios
				.post(`${this.url}/metric-mapping-info-validation`, {...params})
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	postGpsMetricMapping = (metricsMappings, dateFormat = "", dataSource = "api") => {
		return new Promise((resolve, reject) => {
			let params = {
				mapping_configs: metricsMappings,
				date_format: dateFormat,
				data_source: dataSource
			};
			axios
				.post(`${this.url}/metric-mapping-info`, {...params})
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	deleteGpsMetricMapping = (metricIds) => {
		return new Promise((resolve, reject) => {
			let params = {
				metric_ids: metricIds
			};
			axios
				.delete(`${this.url}/metric-mapping-info`, {data: {...params}})
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	getAllGpsMetrics = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/gps-metrics`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	getTestingMetricNames = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/testingmetrics`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	deleteThirdPartyOnboarding = (service) => {
		return new Promise((resolve, reject) => {
			axios
				.delete(`${this.url}/third-party-onboarding`, { data: { service } })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	getWellnessTableDrilldown = (metrics, dates, position, playerGroups) => {
		let params = {
			metrics,
			dates,
			position,
			player_group: playerGroups
		};

		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/wellness-table-drill`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	getSessionNaming = (start_date, end_date) => {
		return new Promise((resolve, reject) => {
			if (!start_date || !end_date) {
				reject({
					response: {
						status: 400,
						data: "Both start_date and end_date parameters are required"
					}
				});
				return;
			}

			axios
				.get(`${this.url}/session-naming`, {
					params: { start_date, end_date }
				})
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	updateSessionNaming = (updates) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/session-naming`, updates)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	getPlayerSessionData = (date) => {
		// api/session-player-management

		return new Promise((resolve, reject) => {
			if (!date) {
				reject({
					response: {
						status: 400,
						data: "Date parameter is required"
					}
				});
				return;
			}

			axios
				.get(`${this.url}/session-player-management`, {
					params: { date }
				})
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	updatePlayerSessionManagement = (payload) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/session-player-management`, payload)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	getTrainingDates = (provider, numDates) => {
		return new Promise((resolve, reject) => {
			const params = {
				provider,
				num_dates: numDates
			};
			axios
				.get(`${this.url}/get_training_dates`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e);
				});
		});
	}

	getFlagReminderConfig = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/flag-reminder-config`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	postFlagReminderConfig = (data) => {	
		return new Promise((resolve, reject) => {
			let params = {
				...data
			};

			axios
				.post(`${this.url}/flag-reminder-config`, {...params})
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	patchFlagReminderConfig = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.patch(`${this.url}/flag-reminder-config`, data)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	getFocusedMetrics = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/focused_metrics`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	getUserDefinedMetrics = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/user-defined-metrics`)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	postUserDefinedMetric = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/user-defined-metrics`, data)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	patchUserDefinedMetric = (id, data) => {
		return new Promise((resolve, reject) => {
			axios
				.patch(`${this.url}/user-defined-metrics/${id}`, data)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	deleteUserDefinedMetric = (id) => {
		return new Promise((resolve, reject) => {
			axios
				.delete(`${this.url}/user-defined-metrics/${id}`)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	// ── Active Datapoints endpoints ──

	getActiveDatapoints = (date) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/active_datapoints`, {
					params: { date }
				})
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	updateActiveDatapoints = (player_ids, start_date, end_date, is_active) => {
		const data = { player_ids, start_date, end_date, is_active };
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/active_datapoints`, data)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	// ── Email ──

	// Generic email sender: subject, body and recipients come from the caller, with an
	// optional attachment. Multipart, with one repeated `emails` key per recipient
	// (read server-side with getlist).
	sendEmail = ({ emails, subject, message, file }) => {
		const formData = new FormData();
		(emails || []).forEach((email) => formData.append("emails", email));
		formData.append("subject", subject);
		formData.append("message", message);
		if (file) formData.append("file", file);

		return axios.post(`${this.url}/send-email`, formData).then((x) => x.data);
	}

	// ── Dashboard endpoints ──

	listDashboards = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/dashboard`)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	createDashboard = ({ name, info }) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/dashboard`, { name, info })
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	getDashboardSpec = (dashboardId) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/dashboard/${dashboardId}`)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	// The dashboard rendered to PDF by the server renderer, as a Blob.
	renderDashboardPdf = (dashboardId) => {
		return axios
			.post(`${this.url}/dashboard/${dashboardId}/pdf`, null, { responseType: "blob" })
			.then((x) => x.data);
	}

	getDashboardData = (dashboardId, params) => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/dashboard-data/${dashboardId}`, { params })
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	patchDashboard = (dashboardId, { name, info }) => {
		return new Promise((resolve, reject) => {
			axios
				.patch(`${this.url}/dashboard/${dashboardId}`, { name, info })
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	duplicateDashboard = (dashboardId) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/dashboard/${dashboardId}/duplicate`)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	deleteDashboard = (dashboardId) => {
		return new Promise((resolve, reject) => {
			axios
				.delete(`${this.url}/dashboard/${dashboardId}`)
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	validateDashboardSchema = (payload) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/dashboard/validate-schema`, { type: "DashboardSpec", value: payload })
				.then((x) => resolve(x.data))
				.catch((e) => reject(e));
		});
	}

	// AI Insights widget → V2 chatbot. Always SSE on 200; every gate/payload
	// failure is a JSON body with a real status, sent before the first byte — so
	// this returns the raw Response and the caller branches on `ok`.
	// fetch (not axios 0.21) for the stream body + the caller's abort signal.
	postDashboardInsightsStream = async ({ dashboardId, dashboardSpec, dashboardData, widgetId, force, signal }) => {
		// fetch bypasses the axios interceptor that keeps Authorization fresh
		// after token expiry, so pull a current token; fall back to the axios
		// default header (the pre-expiry behavior) if the refresh fails.
		const token = await getFreshAccessToken().catch(() => null);
		return fetch(`${this.url}/dashboard-insights`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				...axios.defaults.headers.common,
				...(token ? { Authorization: token } : {}),
			},
			body: JSON.stringify({
				dashboard_id: dashboardId,
				dashboard_spec: dashboardSpec,
				dashboard_data: dashboardData,
				widget_id: widgetId,
				force: !!force,
			}),
			signal,
		});
	}

	// ── Automatic Actions endpoints ──

	getAutomaticActions = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/automatic-action`, { })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e);
				});
		});
	}

	postAutomaticActions = (data) => {
		return new Promise((resolve, reject) => {
			let params = {
				...data
			};

			axios
				.post(`${this.url}/automatic-action`, { ...params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	patchAutomaticActions = (data) => {
		return new Promise((resolve, reject) => {
			let params = {
				...data
			};

			axios
				.patch(`${this.url}/automatic-action`, { ...params })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	deleteAutomaticActions = (id) => {
		return new Promise((resolve, reject) => {
			axios
				.delete(`${this.url}/automatic-action`, { data: { id } })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	getAutomaticActionTypes = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/automatic-action-type`, { })
				.then((x) => resolve(x.data))
				.catch((e) => {
					console.error(e);
					reject(e);
				});
		});
	}

	getUserDefinedFlags = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/user-defined-flags`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	createUserDefinedFlag = (data) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/user-defined-flags`, data)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	updateUserDefinedFlag = (id, data) => {
		return new Promise((resolve, reject) => {
			axios
				.patch(`${this.url}/user-defined-flags/${id}`, data)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	deleteUserDefinedFlag = (id) => {
		return new Promise((resolve, reject) => {
			axios
				.delete(`${this.url}/user-defined-flags/${id}`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	backTestUserDefinedFlag = (config, startDate, endDate, filters) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/user-defined-flags/back-test`, { config, start_date: startDate, end_date: endDate, filters })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	validateUserDefinedFlagCondition = (condition) => {
		return new Promise((resolve, reject) => {
			axios
				.post(`${this.url}/user-defined-flags/validate-condition`, { condition })
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	getBanners = () => {
		return new Promise((resolve, reject) => {
			axios
				.get(`${this.url}/banners`)
				.then((x) => resolve(x.data))
				.catch((e) => {
					reject(e);
				});
		});
	}

	getPlatformUsage = () => {
		return axios.get(`${this.url}/platform-usage`).then((x) => x.data);
	};

	refreshPlatformUsage = () => {
		return axios.post(`${this.url}/platform-usage/refresh`).then((x) => x.data);
	};

	// --- RTP Planner (docs/superpowers/specs/2026-08-29-rtp-planner-api-reference.md) ---
	// These return the axios promise directly (not the swallow-and-reject wrapper above)
	// so rejections carry the axios error and the UI can read e.response.data.

	getRtpPlans = ({ playerId, status } = {}) => {
		return axios
			.get(`${this.url}/rtp/plans`, { params: { player_id: playerId, status } })
			.then((x) => x.data);
	}

	getRtpTemplates = () => {
		return axios.get(`${this.url}/rtp/templates`).then((x) => x.data);
	}

	// -> { canCreate, templates: [Entry, ...] }, sorted by title
	getRtpBlockTemplates = () => {
		return axios.get(`${this.url}/rtp/block-templates`).then((x) => x.data);
	}

	// body: { title, content } or { title, blockId }
	createRtpBlockTemplate = (body) => {
		return axios.post(`${this.url}/rtp/block-templates`, body).then((x) => x.data);
	}

	updateRtpBlockTemplate = (id, body) => {
		return axios.patch(`${this.url}/rtp/block-templates/${id}`, body).then((x) => x.data);
	}

	deleteRtpBlockTemplate = (id) => {
		return axios.delete(`${this.url}/rtp/block-templates/${id}`).then((x) => x.data);
	}

	createRtpPlan = (body) => {
		return axios.post(`${this.url}/rtp/plans`, body).then((x) => x.data);
	}

	getRtpPlan = (planId) => {
		return axios.get(`${this.url}/rtp/plans/${planId}`).then((x) => x.data);
	}

	patchRtpPlan = (planId, body) => {
		return axios.patch(`${this.url}/rtp/plans/${planId}`, body).then((x) => x.data);
	}

	rtpPlanAction = (planId, body) => {
		return axios.post(`${this.url}/rtp/plans/${planId}`, body).then((x) => x.data);
	}

	getRtpPlanNotes = (planId) => {
		return axios.get(`${this.url}/rtp/plans/${planId}/notes`).then((x) => x.data);
	}

	getRtpPlanEvents = (planId, page = 1, pageSize = 50) => {
		return axios
			.get(`${this.url}/rtp/plans/${planId}/events`, { params: { page, page_size: pageSize } })
			.then((x) => x.data);
	}

	patchRtpPhase = (phaseId, body) => {
		return axios.patch(`${this.url}/rtp/phases/${phaseId}`, body).then((x) => x.data);
	}

	deleteRtpPhase = (phaseId) => {
		return axios.delete(`${this.url}/rtp/phases/${phaseId}`).then((x) => x.data);
	}

	rtpPhaseAction = (phaseId, body) => {
		return axios.post(`${this.url}/rtp/phases/${phaseId}`, body).then((x) => x.data);
	}

	patchRtpCriterion = (criterionId, body) => {
		return axios.patch(`${this.url}/rtp/criteria/${criterionId}`, body).then((x) => x.data);
	}

	getHelmetImpacts = ({ start_date, end_date, positions = [], player_groups = [], player_id }) => {
		return axios
			.get(`${this.url}/helmet-impacts`, { params: { start_date, end_date, positions, player_groups, player_id } })
			.then((x) => x.data);
	}

	updateHelmetImpact = (impactId, body) => {
		return axios.patch(`${this.url}/helmet-impacts/${impactId}`, body).then((x) => x.data);
	}

	deleteHelmetImpact = (impactId) => {
		return axios.delete(`${this.url}/helmet-impacts/${impactId}`).then((x) => x.data);
	}

	deleteRtpCriterion = (criterionId) => {
		return axios.delete(`${this.url}/rtp/criteria/${criterionId}`).then((x) => x.data);
	}

	rtpCriterionAction = (criterionId, body) => {
		return axios.post(`${this.url}/rtp/criteria/${criterionId}`, body).then((x) => x.data);
	}

	patchRtpBlock = (blockId, body) => {
		return axios.patch(`${this.url}/rtp/blocks/${blockId}`, body).then((x) => x.data);
	}

	deleteRtpBlock = (blockId) => {
		return axios.delete(`${this.url}/rtp/blocks/${blockId}`).then((x) => x.data);
	}

	patchRtpNote = (noteId, body) => {
		return axios.patch(`${this.url}/rtp/notes/${noteId}`, body).then((x) => x.data);
	}

	// -> [{ label, value }, ...]
	getRtpMetricStats = (playerId, metricKey) => {
		return axios
			.get(`${this.url}/rtp/metric-stats`, { params: { playerId, metricIdentifier: metricKey } })
			.then((x) => x.data);
	}

	// -> { metricIdentifier, aggregation?, dailyValues: [{ date, value|null }], markers: [{ key, label, value }] }
	getRtpMetricProgress = (planId, metricKey) => {
		return axios
			.get(`${this.url}/rtp/metric-progress`, { params: { planId, metricIdentifier: metricKey } })
			.then((x) => x.data);
	}

	deleteRtpNote = (noteId) => {
		return axios.delete(`${this.url}/rtp/notes/${noteId}`).then((x) => x.data);
	}

	// The schedule PDF goes through the general report endpoint. generateGeneralReport is not
	// used: it requires free-text `emails`, and this report accepts staff uuids only.
	// mode "pdf" saves the file, or with returnFile resolves { buffer, name } (for the annotator).
	// mode "email" resolves with the backend's success text. Rejects with the backend's error text.
	exportRtpSchedule = async (body, { returnFile = false } = {}) => {
		const pdf = body.mode === "pdf";
		try {
			const response = await axios.post(
				`${this.url}/generate-report`,
				{ report_type: "rtp_schedule", ...body },
				{ timeout: 180000, ...(pdf ? { responseType: "blob" } : {}) }
			);
			if (!pdf) return response.data;
			const match = /filename="?([^";]+)"?/i.exec(response.headers["content-disposition"] ?? "");
			const name = match ? match[1] : "RTP_schedule.pdf";
			if (returnFile) return { buffer: await response.data.arrayBuffer(), name };
			const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", name);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
			return null;
		} catch (e) {
			throw await readErrorMessage(e?.response?.data);
		}
	}

	// The notes calls reject with the axios error. The error body is { error, field }.
	getNotes = (from, to) => {
		return axios.get(`${this.url}/notes`, { params: { from, to } }).then((x) => x.data);
	};

	createNote = (data) => {
		return axios.post(`${this.url}/notes`, data).then((x) => x.data);
	};

	updateNote = (uuid, data) => {
		return axios.patch(`${this.url}/notes/${uuid}`, data).then((x) => x.data);
	};

	deleteNote = (uuid) => {
		return axios.delete(`${this.url}/notes/${uuid}`).then((x) => x.data);
	};

	getNoteTags = () => {
		return axios.get(`${this.url}/notes/tags`).then((x) => x.data);
	};

	createNoteTag = (data) => {
		return axios.post(`${this.url}/notes/tags`, data).then((x) => x.data);
	};

	deleteNoteTag = (id) => {
		return axios.delete(`${this.url}/notes/tags/${id}`).then((x) => x.data);
	};

	getNoteAttributes = () => {
		return axios.get(`${this.url}/note-attributes`).then((x) => x.data);
	};

	createNoteAttribute = (data) => {
		return axios.post(`${this.url}/note-attributes`, data).then((x) => x.data);
	};

	updateNoteAttribute = (id, data) => {
		return axios.patch(`${this.url}/note-attributes/${id}`, data).then((x) => x.data);
	};

	deleteNoteAttribute = (id) => {
		return axios.delete(`${this.url}/note-attributes/${id}`).then((x) => x.data);
	};
}

export const trainingAPI = new TrainingAPI();
